import React, { useRef, useEffect } from "react";
import Styles from "./TermsCondition.module.css";
import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import "@wojtekmaj/react-timerange-picker/dist/TimeRangePicker.css";
import "react-clock/dist/Clock.css";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../../../commons/ConfirmationModal/ConfirmationModal";
import ChargeSelection from "../ChargeSelection/ChargeSelection";
import useTermsCondition from "./hooks/useTermsCondition";
import CounterpartySection from "./components/CounterpartySection";

const TermsCondition = ({
  open,
  handleClose,
  isEditTerms = false,
  listingId,
  type,
  useSecondaryAccent = false,
}) => {
  const {
    // State
    uniqueSelfReq,
    selectedSelfReq,
    setSelectedSelfReq,
    loading,
    conformOpen,
    setConformOpen,
    dataWithIndex,
    rejectAllTermsConfirm,
    setRejectAllTermsConfirm,
    rejectLoading,
    selectedBids,
    fieldErrors,
    setFieldErrors,
    chargeModal,
    setChargeModal,
    chargeDetails,
    setChargeDetails,
    rejectContract,
    setRejectContract,
    // Redux
    selfEntityId,
    listingType,
    mainScreenMatchData,
    user,
    // Handlers
    handleInputChange,
    handleEnergyRateTypeChange,
    handleUserSelectBid,
    handleCompanyToggle,
    handleTermsSubmit,
    handleChargeFinal,
    handleDirectTermsSumbit,
    handleRejectAllTermsSubmit,
    // Computed helpers
    getCounterparties,
    getCurrentValues,
    isCompanyCollapsed,
    isRequisitionCollapsed,
    isAllSlotsSelected,
    calculateBucketFillPercentage,
    hasFieldErrors,
    goToPrevReq,
    goToNextReq,
    getAllFilledFinalValues,
    validateStartDateTimes,
  } = useTermsCondition({ open, handleClose, isEditTerms, listingId, type });
  const modalTitle =
    type === "accept"
      ? "Approve Finalized T&C"
      : isEditTerms
        ? "Edit T&C"
        : "Finalize T&C";

  const currentReqIndex = uniqueSelfReq.findIndex(
    (req) => req.selfRequisitionIndex === selectedSelfReq?.selfRequisitionIndex,
  );

  // Scroll active tab into view whenever the selection changes
  const activeTabRef = useRef(null);
  useEffect(() => {
    activeTabRef.current?.scrollIntoView({
      behavior: "smooth",
      inline: "nearest",
      block: "nearest",
    });
  }, [selectedSelfReq]);

  return (
    <>
      {chargeModal && (
        <ChargeSelection
          useSecondaryAccent={useSecondaryAccent}
          loading={loading}
          open={chargeModal}
          handleClose={() => setChargeModal(false)}
          handleSubmit={handleChargeFinal}
          details={chargeDetails}
          handleDirectSubmit={handleDirectTermsSumbit}
          type={type}
          setRejectContract={setRejectContract}
          setRejectAllTermsConfirm={setRejectAllTermsConfirm}
          mainScreenMatchData={mainScreenMatchData}
        />
      )}

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <div
          className={`popupModal ${Styles.modalMainCont} ${
            useSecondaryAccent ? "secondaryAccent" : ""
          }`}
        >
          {/* ── Header ────────────────────────────────────────────── */}
          <div className="modalHeading">
            <div style={{ color: "var(--fourth-color-text)", fontWeight: 500 }}>
              {modalTitle}
            </div>
            <Close
              onClick={handleClose}
              style={{ cursor: "pointer" }}
              alterbutton="false"
            />
          </div>

          <div className={Styles.termsMainContainer}>
            {/* ── Requisition tabs ──────────────────────────────── */}
            <div className={Styles.reqTabsContainer}>
              {/* Left arrow — stays pinned, never scrolls */}
              <button
                className={Styles.arrowBtn}
                onClick={goToPrevReq}
                disabled={currentReqIndex === 0}
              >
                &laquo;
              </button>

              {/* Scrollable tabs strip */}
              <div className={Styles.reqTabsScroll}>
                {uniqueSelfReq.map((req) => {
                  const isActive =
                    selectedSelfReq?.selfRequisitionIndex ===
                    req.selfRequisitionIndex;
                  return (
                    <button
                      key={req.selfRequisitionIndex}
                      ref={isActive ? activeTabRef : null}
                      className={isActive ? Styles.reqTabActive : Styles.reqTab}
                      onClick={() => setSelectedSelfReq(req)}
                    >
                      {`Requisition ${req.reqId}`}
                    </button>
                  );
                })}
              </div>

              {/* Right arrow — stays pinned, never scrolls */}
              <button
                className={Styles.arrowBtn}
                onClick={goToNextReq}
                disabled={currentReqIndex === uniqueSelfReq.length - 1}
              >
                &raquo;
              </button>
            </div>

            {/* ── Table header ──────────────────────────────────── */}
            <div className={Styles.tableHeadingCustom}>
              <div
                style={{
                  flexBasis: "210px",
                  minWidth: "210px",
                  maxWidth: "210px",
                  width: "210px",
                }}
              >
                Category
              </div>
              <div>My Listing</div>
              <div style={{ paddingRight: "8px" }}>Counterparty Listing</div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingLeft: "8px",
                }}
                className={Styles.finalValues}
              >
                Final Values
                <div>
                  {selectedSelfReq && (
                    <div className={Styles.bucketFillContainer}>
                      <div className={Styles.bucketCircle}>
                        <svg width="35" height="35" viewBox="0 0 40 40">
                          <circle
                            cx="20"
                            cy="20"
                            r="16"
                            fill="none"
                            stroke="#E0E0E0"
                            strokeWidth="3"
                          />
                          <circle
                            cx="20"
                            cy="20"
                            r="16"
                            fill="none"
                            stroke="#00BF6F"
                            strokeWidth="3"
                            strokeDasharray={`${
                              (calculateBucketFillPercentage(
                                selectedSelfReq.id,
                              ) *
                                100.5) /
                              100
                            } 100.5`}
                            strokeLinecap="round"
                            transform="rotate(-90 20 20)"
                            className={Styles.progressCircle}
                          />
                          <text
                            x="20"
                            y="23"
                            textAnchor="middle"
                            fontSize="9"
                            fontWeight="600"
                            fill="#00BF6F"
                          >
                            {calculateBucketFillPercentage(selectedSelfReq.id)}%
                          </text>
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ── Counterparty list ─────────────────────────────── */}
            {selectedSelfReq &&
              dataWithIndex[0]?.matches?.[selectedSelfReq.id] &&
              getCounterparties(
                dataWithIndex[0].matches[selectedSelfReq.id],
              ).map((item) => {
                const matchSlotsCount = item.matchSlots?.length || 1;
                const currentValues = getCurrentValues(item, matchSlotsCount);
                const selfSlot =
                  dataWithIndex[0].matches[selectedSelfReq.id].selfSlot;

                const hasCounterTraderId = item.traderId;

                return (
                  <CounterpartySection
                    key={item.connectId}
                    item={item}
                    selfSlot={selfSlot}
                    matchSlotsCount={matchSlotsCount}
                    selectedSelfReq={selectedSelfReq}
                    isEditTerms={isEditTerms}
                    user={user}
                    listingType={listingType}
                    currentValues={currentValues}
                    fieldErrors={fieldErrors}
                    selectedBids={selectedBids}
                    dataWithIndex={dataWithIndex}
                    isAllSlotsSelected={isAllSlotsSelected}
                    isCompanyCollapsed={isCompanyCollapsed}
                    isRequisitionCollapsed={isRequisitionCollapsed}
                    handleCompanyToggle={handleCompanyToggle}
                    handleUserSelectBid={handleUserSelectBid}
                    handleInputChange={handleInputChange}
                    handleEnergyRateTypeChange={handleEnergyRateTypeChange}
                    setFieldErrors={setFieldErrors}
                  />
                );
              })}
          </div>

          {/* ── Action buttons ────────────────────────────────────── */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "16px",
              padding: "7px 25px",
            }}
          >
            <div style={{ display: "flex", gap: "16px" }}>
              {/* Cancel */}
              <button
                style={{
                  padding: "12px 24px",
                  border: "none",
                  background: "var(--disabled-color-background)",
                  color: "#808080ff",
                  borderRadius: "6px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
                onClick={handleClose}
              >
                Cancel
              </button>

              {/* Reject All — only in accept flow */}
              {type === "accept" && (
                <button
                  style={{
                    padding: "12px 24px",
                    backgroundColor:
                      "var(--warning-background-thin) !important",
                    color: "var(--danger-shade-text)",
                    border: "1px solid var(--danger-shade-text)",
                    borderRadius: "6px",
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setRejectContract({
                      contractIds: mainScreenMatchData?.map((ele) => ({
                        contractId: ele?.id,
                        consumerId: ele?.capacityConsumer?.consumerId,
                        consumerName: ele?.capacityConsumer?.consumerName,
                        generatorId: ele?.capacityGenerator?.generatorId,
                        generatorName: ele?.capacityGenerator?.generatorName,
                      })),
                    });
                    setRejectAllTermsConfirm(true);
                  }}
                >
                  Reject All
                </button>
              )}

              {/* Continue / Submit */}
              <button
                disabled={loading || hasFieldErrors()}
                style={{
                  padding: "12px 24px",
                  border: "none",
                  background: hasFieldErrors()
                    ? "#cccccc"
                    : "var(--primary-color-text)",
                  color: hasFieldErrors() ? "#666666" : "#fff",
                  borderRadius: "6px",
                  fontWeight: 500,
                  cursor:
                    loading || hasFieldErrors() ? "not-allowed" : "pointer",
                  opacity: hasFieldErrors() ? 0.6 : 1,
                }}
                title={
                  hasFieldErrors()
                    ? "Please fix all errors before continuing"
                    : ""
                }
                onClick={() => {
                  const filledValues = getAllFilledFinalValues();
                  if (!filledValues) return;
                  if (!validateStartDateTimes(filledValues)) {
                    return;
                  }
                  if (selectedBids.length === 0) {
                    toast.error("Please select at least one requisition.");
                    return;
                  }
                  // Extract minimum FIXED energy rate to enforce trader charge ceiling
                  let minFixedRate = null;
                  Object.values(filledValues).forEach((reqData) => {
                    Object.values(reqData).forEach((slotData) => {
                      Object.entries(slotData).forEach(([key, val]) => {
                        if (/^req\d+_energyRate$/.test(key)) {
                          const reqNum = key.match(/\d+/)[0];
                          if (
                            slotData[`req${reqNum}_energyRateStr`] === "FIXED"
                          ) {
                            const n = Number(val);
                            if (!isNaN(n) && n > 0) {
                              minFixedRate =
                                minFixedRate === null
                                  ? n
                                  : Math.min(minFixedRate, n);
                            }
                          }
                        }
                      });
                    });
                  });

                  setChargeDetails({
                    contractIds: mainScreenMatchData?.map((ele) => ({
                      contractId: ele?.id,
                      consumerId: ele?.capacityConsumer?.consumerId,
                      consumerName: ele?.capacityConsumer?.consumerName,
                      generatorId: ele?.capacityGenerator?.generatorId,
                      generatorName: ele?.capacityGenerator?.generatorName,
                    })),
                    selfId: selfEntityId,
                    counterIds: selectedBids?.map((ele) => ({
                      counterName: ele?.company,
                      counterId: ele?.counterpartyEntityId,
                      name: ele?.name,
                      ...(ele?.traderId ? { traderId: ele.traderId } : {}),
                    })),
                    energyRate: minFixedRate,
                  });
                  setChargeModal(true);
                }}
              >
                {loading ? "Submitting..." : "Continue"}
              </button>

              {/* Finalize / edit confirmation modal */}
              <ConfirmationModal
                secondaryHover={useSecondaryAccent}
                open={conformOpen}
                loading={loading}
                handleClose={() => setConformOpen(false)}
                handleSubmit={(val) => {
                  const filledValues = getAllFilledFinalValues();
                  if (!filledValues) return;
                  if (isEditTerms) {
                    handleTermsSubmit(filledValues, { isEdit: true });
                  } else {
                    handleChargeFinal(val);
                  }
                }}
                header={
                  type === "accept"
                    ? "Approve Terms Confirmation"
                    : isEditTerms
                      ? "Update Terms Confirmation"
                      : "Finalize Terms Confirmation"
                }
                data={
                  type === "accept" ? (
                    <>
                      Once the Terms & Conditions are accepted, no further
                      changes will be allowed.{" "}
                      <b>Initial Margin will be blocked.</b> Do you want to
                      continue?
                    </>
                  ) : isEditTerms ? (
                    "You are about to update previously finalized T&C. This will overwrite existing finalized values. Do you want to continue?"
                  ) : (
                    `The T&C will be shared with the ${user === "consumer" ? "seller" : "buyer"} for final approval. All other pending connection requests will be auto-rejected. Do you want to continue?`
                  )
                }
              />

              {/* Reject all confirmation modal */}
              <ConfirmationModal
                secondaryHover={useSecondaryAccent}
                open={rejectAllTermsConfirm}
                handleClose={() => setRejectAllTermsConfirm(false)}
                handleSubmit={() => {
                  handleDirectTermsSumbit(
                    rejectContract?.contractIds,
                    "REJECTED",
                  );
                  handleRejectAllTermsSubmit();
                }}
                header={"Confirm Rejection of Terms"}
                data={
                  "Declining will cancel this transaction. Do you want to continue?"
                }
                loading={rejectLoading}
              />
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TermsCondition;
